/**
 * Anti-corruption layer for the lot map inventory API response.
 *
 * @module features/nativeInteractiveSiteMap/services/processLotMapInventory
 */

function mapLots(availableLots) {
  // Maps only the additive fields written by fetchLotInventorySuccess.
  // status, housePlanIds, and housingUnitId are site-plan-owned and remain
  // untouched during enrichment, so they are not mapped here.
  // sitePlanLotId is the join key back to the site-plan-owned lot (a string, e.g. 'g1_l140').
  // lotId is the integer id housePlans (Lots[]) and housingUnits (LotId) reference lots by —
  // a different id space than sitePlanLotId.
  return (availableLots ?? []).map((lot) => ({
    sitePlanLotId: lot.SitePlanLotId,
    lotId:         lot.LotId ?? null,
    lotSize:       lot.LotSize ?? null,
    formattedLotSize: lot.FormattedLotSize ?? null,
    displayLabel:  lot.DisplayLabel ?? null,
    lotGroupName:  lot.LotGroupName ?? null,
    lotType:       lot.LotType ?? null,
    lotFacing:     lot.LotFacing ?? null,
  }));
}

function mapHomeFeatures(homeFeatures) {
  return (homeFeatures ?? []).map((h) => ({
    description:    h.Description ?? null,
    highlightGroup: h.HighlightGroup ?? null,
    codeId:         h.CodeId ?? null,
  }));
}

function mapPlanImages(images) {
  return (images ?? []).map((img) => ({
    title:       img.Title ?? null,
    url:         img.Url ?? null,
    description: img.Description ?? null,
    displayOrder: img.DisplayOrder ?? null,
  }));
}

function mapHousePlans(housePlans) {
  return (housePlans ?? []).map((plan) => ({
    planId:             plan.PlanId,
    name:               plan.Name ?? null,
    price:              plan.Price ?? null,
    formattedPrice:     plan.FormattedPrice ?? null,
    bedrooms:           plan.Bedrooms ?? null,
    bathrooms:          plan.Bathrooms ?? null,
    formattedBathrooms: plan.FormattedBathrooms ?? null,
    halfBathroomsCount: plan.HalfBathroomsCount ?? null,
    squareFeet:         plan.SquareFeet ?? null,
    formattedSquareFeet: plan.FormattedSquareFeet ?? null,
    garage:             plan.Garage ?? null,
    image:              plan.Image ?? null,
    listingUrlPath:     plan.ListingUrlPath ?? null,
    lotIds:             plan.Lots ?? [],
    homeFeatures:       mapHomeFeatures(plan.HomeFeatures),
    elevations:         mapPlanImages(plan.Elevations),
    floorplans:         mapPlanImages(plan.Floorplans),
  }));
}

function mapHousingUnits(housingUnits) {
  return (housingUnits ?? []).map((unit) => ({
    specificationId:    unit.SpecificationId,
    lotId:              unit.LotId ?? null,
    name:               unit.Name ?? null,
    price:              unit.Price ?? null,
    formattedPrice:     unit.FormattedPrice ?? null,
    bedrooms:           unit.Bedrooms ?? null,
    bathrooms:          unit.Bathrooms ?? null,
    formattedBathrooms: unit.FormattedBathrooms ?? null,
    halfBathroomsCount: unit.HalfBathroomsCount ?? null,
    squareFeet:         unit.SquareFeet ?? null,
    formattedSquareFeet: unit.FormattedSquareFeet ?? null,
    garage:             unit.Garage ?? null,
    image:              unit.Image ?? null,
    listingUrlPath:     unit.ListingUrlPath ?? null,
    homeFeatures:       mapHomeFeatures(unit.HomeFeatures),
    elevations:         mapPlanImages(unit.Elevations),
    floorplans:         mapPlanImages(unit.Floorplans),
  }));
}

function mapSoldLots(soldLots) {
  // Sold lots are their own section of the response — unlike housePlans/housingUnits, they are
  // never referenced by a house-plan/housing-unit's lotIds/LotId, so no cross-referencing is
  // needed here. Shares the same lot fields as mapLots (sitePlanLotId, lotId, lotSize,
  // displayLabel, lotGroupName, lotType, lotFacing) plus the assigned plan's sold-home attributes.
  return (soldLots ?? []).map((lot) => ({
    lotId:                lot.LotId ?? null,
    lotStatusType:        lot.LotStatusType ?? null,
    lotSize:              lot.LotSize ?? null,
    displayLabel:         lot.DisplayLabel ?? null,
    lotGroupName:         lot.LotGroupName ?? null,
    sitePlanLotId:        lot.SitePlanLotId,
    lotType:              lot.LotType ?? null,
    lotFacing:            lot.LotFacing ?? null,
    housePlanIds:         lot.HousePlanIds ?? [],
    housingUnitId:        lot.HousingUnitId ?? null,
    soldHousePlanSize:    lot.SoldHousePlanSize ?? null,
    formattedSquareFeet:  lot.FormattedSquareFeet ?? null,
    soldHousePlanAddress: lot.SoldHousePlanAddress ?? null,
    soldHousePlanBaths:   lot.SoldHousePlanBaths ?? null,
    formattedBathrooms:   lot.FormattedBathrooms ?? null,
    soldHousePlanBeds:    lot.SoldHousePlanBeds ?? null,
    soldHousePlanGarage:  lot.SoldHousePlanGarage ?? null,
    soldHousePlanPrice:   lot.SoldHousePlanPrice ?? null,
    formattedPrice:       lot.FormattedPrice ?? null,
  }));
}

/**
 * Maps the lot map inventory API response to the normalized Redux store shape.
 * Always returns a valid shape — null/undefined input yields empty arrays so
 * the epic can dispatch fetchLotInventorySuccess unconditionally without a
 * filter(Boolean) that would leave status stuck at "loading".
 *
 * @param {object} rawResponse - Raw PascalCase API response
 * @returns {{ lots: object[], housePlans: object[], housingUnits: object[], soldLots: object[] }}
 */
function processLotMapInventory(rawResponse) {
  return {
    lots:         mapLots(rawResponse?.AvailableLots),
    housePlans:   mapHousePlans(rawResponse?.HousePlans),
    housingUnits: mapHousingUnits(rawResponse?.HousingUnits),
    soldLots:     mapSoldLots(rawResponse?.SoldLots),
  };
}

export default processLotMapInventory;
